import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { Menu, X, ChevronDown, ShieldCheck, ClipboardList, ShoppingBag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/lib/cart";

function CartLink({ onClick, className }: { onClick?: () => void; className?: string }) {
  const { count } = useCart();
  return (
    <Link
      to="/cart"
      onClick={onClick}
      aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`}
      className={
        (className ??
          "relative inline-flex items-center justify-center min-h-11 min-w-11 rounded-md text-foreground/80 transition-colors hover:text-[var(--color-ember)]") +
        " " +
        focusRing
      }
      activeProps={{ className: "text-[var(--color-ember)]", "aria-current": "page" }}
    >
      <ShoppingBag className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 min-w-[1.15rem] rounded-full bg-[var(--color-ember)] px-1 text-center text-[10px] font-bold leading-[1.15rem] text-[var(--color-ember-foreground)]">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}


function useIsAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) {
        if (!cancelled) setIsAdmin(false);
        return;
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userRes.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!cancelled) setIsAdmin(!!data);
    }
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        check();
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);
  return isAdmin;
}

function AdminLink({ onClick, className }: { onClick?: () => void; className?: string }) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return null;
  return (
    <Link
      to="/admin/reviews"
      onClick={onClick}
      className={
        (className ??
          "inline-flex items-center gap-1.5 rounded-md border border-[var(--color-ember)]/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)] hover:bg-[var(--color-ember)]/10") +
        " " +
        focusRing
      }
      activeProps={{
        className: "bg-[var(--color-ember)]/10",
        "aria-current": "page",
      }}
    >
      <ShieldCheck className="h-3.5 w-3.5" /> Reviews
    </Link>
  );

}

function useIsSignedIn() {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function check() {
      const { data } = await supabase.auth.getUser();
      if (!cancelled) setSignedIn(!!data.user);
    }
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        check();
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);
  return signedIn;
}

function ReportConditionsLink({ onClick, className }: { onClick?: () => void; className?: string }) {
  const signedIn = useIsSignedIn();
  if (!signedIn) return null;
  return (
    <Link
      to="/report-conditions"
      onClick={onClick}
      className={
        (className ??
          "inline-flex items-center gap-1.5 rounded-md border border-[var(--color-ember)]/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)] hover:bg-[var(--color-ember)]/10") +
        " " +
        focusRing
      }
      activeProps={{
        className: "bg-[var(--color-ember)]/10",
        "aria-current": "page",
      }}
    >
      <ClipboardList className="h-3.5 w-3.5" /> Report Conditions
    </Link>
  );
}


type NavLink = { to: string; label: string };
type NavDropdown = { label: string; to?: string; children: NavLink[] };
type NavItem = NavLink | NavDropdown;

const LEFT_ITEMS: NavItem[] = [
  { to: "/", label: "Home" },
  { to: "/accommodations", label: "Accommodations" },
  {
    label: "Fishing",
    to: "/fishing",
    children: [
      { to: "/fishing", label: "Fishing Overview" },
      { to: "/fishing-report", label: "Fishing Report" },
      { to: "/fish-species", label: "Fish Species" },
      { to: "/weather", label: "Weather" },
    ],
  },
];

const RIGHT_ITEMS: NavItem[] = [
  { to: "/shop", label: "Shop" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  {
    label: "More",
    children: [
      { to: "/about", label: "About" },
      { to: "/travel-tips", label: "Travel Tips" },
      { to: "/faq", label: "FAQ" },
      { to: "/links", label: "Links" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

const BOOK_NOW: NavLink = { to: "/booking", label: "Book Now" };


// Shared focus-visible ring — same treatment on every nav link so keyboard
// users get a consistent, high-contrast focus indicator regardless of which
// surface (top-level, dropdown item, mobile item) they land on.
const focusRing =
  "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm";

const linkClass =
  "nav-link relative whitespace-nowrap text-sm xl:text-base font-medium text-foreground/80 transition-colors hover:text-[var(--color-ember)] " +
  "after:pointer-events-none after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[2px] after:rounded-full after:bg-[var(--color-ember)] " +
  "after:origin-center after:scale-x-0 after:transition-transform after:duration-200 hover:after:scale-x-100 focus-visible:after:scale-x-100 " +
  "focus-visible:text-[var(--color-ember)] " +
  focusRing;

const activeLinkProps = {
  className: "text-foreground after:scale-x-100",
  style: { color: "var(--foreground)" },
  "aria-current": "page" as const,
};


const activeDropdownItemProps = {
  className:
    "bg-[var(--color-parchment)] text-[var(--color-ember)] font-semibold border-[var(--color-ember)]",
  "aria-current": "page" as const,
};

const activeMobileItemProps = {
  className:
    "bg-[var(--color-parchment)] text-[var(--color-ember)] font-semibold border-[var(--color-ember)]",
  "aria-current": "page" as const,
};


function isDropdown(item: NavItem): item is NavDropdown {
  return "children" in item;
}

function useIsPathActive(paths: string[]) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return useMemo(
    () =>
      paths.some((p) =>
        p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/"),
      ),
    [paths, pathname],
  );
}

function DesktopDropdown({
  label,
  to,
  children,
}: {
  label: string;
  to?: string;
  children: NavLink[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activePaths = useMemo(
    () => (to ? [to, ...children.map((c) => c.to)] : children.map((c) => c.to)),
    [to, children],
  );
  const isActive = useIsPathActive(activePaths);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const triggerClassName =
    `${linkClass} inline-flex items-center gap-1 ` +
    (isActive ? "text-[var(--color-ember)] after:scale-x-100" : "");

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {to ? (
        <Link
          to={to}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-current={isActive ? "page" : undefined}
          className={triggerClassName}
          onClick={() => setOpen(false)}
        >
          {label}
          <ChevronDown
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-current={isActive ? "page" : undefined}
          className={triggerClassName}
        >
          {label}
          <ChevronDown
            size={14}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {open && (
        <div
          role="menu"
          className="absolute left-1/2 top-full z-50 min-w-[200px] -translate-x-1/2 pt-2"
        >
          <div className="rounded-md border border-border bg-background shadow-lg py-1.5">
            {children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={
                  "flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-foreground/85 border-l-2 border-transparent transition-colors hover:bg-[var(--color-parchment)] hover:text-[var(--color-ember)] hover:border-[var(--color-ember)] " +
                  focusRing
                }
                activeProps={activeDropdownItemProps}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopItem({ item }: { item: NavItem }) {
  if (isDropdown(item)) {
    return <DesktopDropdown label={item.label} to={item.to} children={item.children} />;
  }
  return (
    <Link
      to={item.to}
      className={linkClass}
      activeProps={activeLinkProps}
      activeOptions={item.to === "/" ? { exact: true } : undefined}
    >
      {item.label}
    </Link>
  );
}

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const allItems = [...LEFT_ITEMS, ...RIGHT_ITEMS];
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Lock background scroll while the mobile menu is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-background/85 backdrop-blur border-b border-border overflow-visible">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 overflow-visible">
        {/* Mobile row: 3-column grid keeps logo perfectly centered */}
        <div className="grid lg:hidden grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center px-1 py-3 pb-4">
          <div className="justify-self-start">
            <button
              type="button"
              className="inline-flex items-center justify-center min-h-11 min-w-11 -ml-1 rounded-md text-primary hover:bg-[var(--color-parchment)]/60 transition-colors"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          <Link
            to="/"
            className="justify-self-center flex h-12 w-12 shrink-0 items-center justify-center transform-none"
            aria-label="Coffman Cove Lodging home"
          >
            <img
              src={logoAsset.url}
              alt="Coffman Cove Lodging"
              className="h-full w-full object-contain"
            />
          </Link>
          <div className="justify-self-end">
            {/* Cart occupies the reserved column, keeping the logo centered */}
            <CartLink onClick={() => setOpen(false)} />
          </div>
        </div>

        {/* Desktop row: links | logo | links */}
        <div className="hidden lg:flex items-center justify-center gap-3 lg:gap-4 xl:gap-10 py-3 pb-8 xl:pb-14 relative overflow-visible">
          <nav className="flex items-center gap-3 lg:gap-4 xl:gap-7">
            {LEFT_ITEMS.map((item) => (
              <DesktopItem key={item.label} item={item} />
            ))}
          </nav>

          <Link
            to="/"
            className="flex items-center justify-center shrink-0 h-24 w-24 lg:h-28 lg:w-28 xl:h-44 xl:w-44 translate-y-3 xl:translate-y-5 relative z-10"
          >
            <img
              src={logoAsset.url}
              alt="Coffman Cove Lodging"
              className="h-full w-full object-contain"
            />
          </Link>

          <nav className="flex items-center gap-3 lg:gap-4 xl:gap-7">
            {RIGHT_ITEMS.map((item) => (
              <DesktopItem key={item.label} item={item} />
            ))}
            <ReportConditionsLink />
            <AdminLink />
            <CartLink className="relative inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground/80 transition-colors hover:text-[var(--color-ember)]" />
            
            <Link
              to={BOOK_NOW.to}
              className="ml-2 inline-flex items-center justify-center rounded-md bg-[var(--color-ember-deep)] px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-[var(--color-ember-deep)]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-background whitespace-nowrap"
            >
              {BOOK_NOW.label}
            </Link>
          </nav>
        </div>


        {open && (
          <div
            id="mobile-nav"
            className="lg:hidden border-t border-border bg-background max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain pb-6 pt-2 flex flex-col gap-1"
          >
            {allItems.map((item) =>
              isDropdown(item) ? (
                <div key={item.label} className="flex flex-col gap-1 pt-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1 px-1">
                    {item.label}
                  </p>
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      onClick={() => setOpen(false)}
                      className={
                        "block rounded-md py-2.5 pl-4 pr-3 text-sm font-medium text-foreground/85 border-l-2 border-transparent transition-colors hover:bg-[var(--color-parchment)] hover:text-[var(--color-ember)] hover:border-[var(--color-ember)] " +
                        focusRing
                      }
                      activeProps={activeMobileItemProps}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={
                    "block rounded-md py-2.5 pl-3 pr-3 text-sm font-medium text-foreground/85 border-l-2 border-transparent transition-colors hover:bg-[var(--color-parchment)] hover:text-[var(--color-ember)] hover:border-[var(--color-ember)] " +
                    focusRing
                  }
                  activeProps={activeMobileItemProps}
                  activeOptions={item.to === "/" ? { exact: true } : undefined}
                >
                  {item.label}
                </Link>
              ),
            )}

            <ReportConditionsLink
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-ember)]/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)]"
            />
            <AdminLink
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-[var(--color-ember)]/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)]"
            />
            <Link
              to={BOOK_NOW.to}
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-[var(--color-ember-deep)] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {BOOK_NOW.label}
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
